import { Box, Text } from '@wix/design-system';
import { DEFAULT_PURCHASE_CYCLE } from '@wix/package-picker-shared-studio';
import {
  DisplayPriceData,
  usePrice,
} from '@wix/premium-purchase-platform/@components';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import s from './StudioPrice.scss';

const CURRENCY_PLACEHOLDER = '¤';

export const STUDIO_PRICE_DATA_HOOKS = {
  root: 'studio-price',
  amount: 'studio-price-amount',
  currency: 'studio-price-currency',
  integer: 'studio-price-integer',
  fraction: 'studio-price-fraction',
  fullPrice: 'studio-price-full-price',
  taxLabel: 'studio-price-tax-label',
};

interface StudioPriceProps {
  productId: string;
  overrideCycleLabel?: string;
  centered?: boolean;
}

export const StudioPrice = ({
  productId,
  overrideCycleLabel,
  centered,
}: StudioPriceProps) => {
  const { data: offering } =
    StudioPackagePickerModule.SDK.studioOfferingApiClient.hooks.useFetchOffering(
      {},
    );
  const {
    value,
    cycleLabel,
    fullPrice,
    showCatalogPrice,
    taxLabel,
    showTaxLabel,
  } = usePrice({
    productId,
    cycle: DEFAULT_PURCHASE_CYCLE,
    display: { inline: false, hideZeroDecimals: false },
    overrideCycleLabel,
  }) as DisplayPriceData;

  const { decimalSeparator = '.', positiveSuffix } =
    offering?.currencySettings ?? {};
  const isCurrencySuffix = Boolean(
    positiveSuffix?.includes(CURRENCY_PLACEHOLDER),
  );
  const currency = (
    <Text
      dataHook={STUDIO_PRICE_DATA_HOOKS.currency}
      size="small"
      weight="normal"
    >
      {value.currency}
    </Text>
  );

  return (
    <Box
      direction="vertical"
      dataHook={STUDIO_PRICE_DATA_HOOKS.root}
      align={centered ? 'center' : undefined}
    >
      <Box
        className={s.priceLine}
        gap="0 6px"
        flexWrap="wrap"
        align={centered ? 'center' : undefined}
      >
        <Box dataHook={STUDIO_PRICE_DATA_HOOKS.amount} className={s.priceLine}>
          {!isCurrencySuffix && currency}
          <Text
            dataHook={STUDIO_PRICE_DATA_HOOKS.integer}
            className={s.integer}
          >
            {value.integer}
          </Text>
          {value.fraction && (
            <Text
              dataHook={STUDIO_PRICE_DATA_HOOKS.fraction}
              size="medium"
              weight="normal"
            >
              {`${decimalSeparator}${value.fraction}`}
            </Text>
          )}
          {isCurrencySuffix && <Box paddingLeft="4px">{currency}</Box>}
        </Box>
        <Text size="small" weight="normal">
          {cycleLabel}
        </Text>
      </Box>
      {showCatalogPrice && fullPrice && (
        <Text
          dataHook={STUDIO_PRICE_DATA_HOOKS.fullPrice}
          size="small"
          weight="normal"
          className={s.fullPrice}
        >
          {fullPrice}
        </Text>
      )}
      {showTaxLabel && taxLabel && (
        <Text
          dataHook={STUDIO_PRICE_DATA_HOOKS.taxLabel}
          size="small"
          secondary
        >
          {taxLabel}
        </Text>
      )}
    </Box>
  );
};
